import { useMemo } from 'react';

/**
 * Pure check: does the given slug-keyed selection land on any (effectively)
 * sold-out node of the tree? Mirrors the hook's walk; used by the package
 * pages to test EVERY included product's selection (the hook itself only
 * walks the active group).
 */
export function isSelectionSoldOut(tree, selections) {
  if (!Array.isArray(tree) || tree.length === 0) return false;

  const effSoldOut = (node) => {
    if (node.sold_out) return true;
    const kids = Array.isArray(node.children) ? node.children : [];
    if (kids.length === 0) return false;
    const groups = new Map();
    for (const c of kids) {
      if (!groups.has(c.attribute_slug)) groups.set(c.attribute_slug, []);
      groups.get(c.attribute_slug).push(c);
    }
    for (const g of groups.values()) {
      if (g.every(effSoldOut)) return true;
    }
    return false;
  };

  let hit = false;
  const walk = (scope) => {
    if (hit || !Array.isArray(scope) || scope.length === 0) return;
    const groups = new Map();
    for (const node of scope) {
      if (!groups.has(node.attribute_slug)) groups.set(node.attribute_slug, []);
      groups.get(node.attribute_slug).push(node);
    }
    for (const [slug, nodes] of groups.entries()) {
      const sel = selections[slug];
      if (sel == null) continue;
      const matched = nodes.find((n) => n.value_id === sel);
      if (!matched) continue;
      if (effSoldOut(matched)) { hit = true; return; }
      walk(matched.children ?? []);
    }
  };
  walk(tree);
  return hit;
}

/**
 * Walk a product's compatibility tree against the current slug-keyed
 * selections and return the constraints + per-node image override.
 *
 * The tree is free-form: children of a node may belong to different
 * attributes. Sibling nodes sharing one attribute act as a value whitelist
 * for that attribute under this branch; sibling groups of different
 * attributes are independent AND-constraints. Each attribute appears at
 * most once on a root-to-leaf path. Attributes not mentioned anywhere on
 * the resolved branch are unrestricted (no key in the allowed map).
 *
 * Inputs
 *   tree       — recursive `[{id, value_id, attribute_slug, children, images}, ...]`
 *                (from `product.compatibility_tree`)
 *   selections — flat map of `{ [attributeSlug]: valueId }`
 *
 * Outputs
 *   enabled                     — false when the product has no tree
 *   allowedValueIdsByAttribute  — `{ [slug]: Set<valueId> }`. Missing key
 *                                 means "no restriction".
 *   soldOutValueIdsByAttribute  — `{ [slug]: Set<valueId> }` of options that
 *                                 are sold out in the current scope (flagged
 *                                 directly, or every child option sold out —
 *                                 e.g. a color whose sizes are all gone).
 *                                 Kept SEPARATE from `allowed`: pages hide
 *                                 these options and clear affected
 *                                 selections explicitly.
 *   selectionSoldOut            — true when any matched node on the current
 *                                 selection path is (effectively) sold out
 *   path                        — array of matched node IDs (parents first)
 *   currentNodeImages           — `[{src, alt}]` of the deepest matched
 *                                 node that has any images
 */
export default function useCompatibilityTree({
  tree = [],
  selections = {},
}) {
  return useMemo(() => {
    const hasTree = Array.isArray(tree) && tree.length > 0;
    if (!hasTree) {
      return {
        enabled: false,
        allowedValueIdsByAttribute: {},
        soldOutValueIdsByAttribute: {},
        selectionSoldOut: false,
        path: [],
        currentNodeImages: [],
      };
    }

    const allowed = {};
    const soldOut = {};
    const path = [];
    const matchedNodes = [];

    // A node is effectively sold out when flagged directly, or when any of
    // its child sibling-groups (an AND-constraint) has EVERY option sold out
    // — e.g. a color whose sizes are all sold out.
    const effSoldOut = (node) => {
      if (node.sold_out) return true;
      const kids = Array.isArray(node.children) ? node.children : [];
      if (kids.length === 0) return false;
      const groups = new Map();
      for (const c of kids) {
        if (!groups.has(c.attribute_slug)) groups.set(c.attribute_slug, []);
        groups.get(c.attribute_slug).push(c);
      }
      for (const g of groups.values()) {
        if (g.every(effSoldOut)) return true;
      }
      return false;
    };

    const walk = (scope) => {
      if (!Array.isArray(scope) || scope.length === 0) return;

      const groups = new Map();
      for (const node of scope) {
        const slug = node.attribute_slug;
        if (!groups.has(slug)) groups.set(slug, []);
        groups.get(slug).push(node);
      }

      for (const [slug, nodes] of groups.entries()) {
        const valueIds = new Set(nodes.map((n) => n.value_id));
        if (allowed[slug] instanceof Set) {
          for (const v of [...allowed[slug]]) {
            if (!valueIds.has(v)) allowed[slug].delete(v);
          }
        } else {
          allowed[slug] = valueIds;
        }

        for (const node of nodes) {
          if (effSoldOut(node)) {
            if (!(soldOut[slug] instanceof Set)) soldOut[slug] = new Set();
            soldOut[slug].add(node.value_id);
          }
        }

        const sel = selections[slug];
        if (sel == null) continue;
        const matched = nodes.find((n) => n.value_id === sel);
        if (!matched) continue;

        path.push(matched.id);
        matchedNodes.push(matched);
        walk(matched.children ?? []);
      }
    };

    walk(tree);

    const selectionSoldOut = matchedNodes.some(effSoldOut);

    let currentNodeImages = [];
    for (let i = matchedNodes.length - 1; i >= 0; i--) {
      const n = matchedNodes[i];
      if (Array.isArray(n.images) && n.images.length > 0) {
        currentNodeImages = n.images.map((img) => ({
          src: img.url ?? img.src,
          alt: '',
        }));
        break;
      }
    }

    return {
      enabled: true,
      allowedValueIdsByAttribute: allowed,
      soldOutValueIdsByAttribute: soldOut,
      selectionSoldOut,
      path,
      currentNodeImages,
    };
  }, [tree, selections]);
}
